import { ITableHeader, showServerError } from "@/utils";
import { CreatedFilters } from "@/services/filters/filters";
import { IFilter } from "@/types";
import { filterClass } from "@/services/filters/filterClass";
import { getAffiliateSearch, getUserSearch } from "@/api/user";
import { getEmployeeMethods } from "@/api/payout";
import { Bill } from "@/api/types/payout";
import { IListItem, InfoCards, usePrepareInfoCards } from "@/components-UI/list/types";
import { computed, ComputedRef } from "vue";

export const useBills = (isDetail: boolean = false): { getHeaders: ComputedRef<ITableHeader[]> } => {
    const headers: ITableHeader[] = [
        { text: 'ID', value: 'id', align: 'center', width: '80px' },
        { text: 'ID счёта', value: 'invoice_id', align: 'center', width: '80px' },
        { text: 'Период оплаты', value: 'invoice_pay_period', width: 'max-content' },
        { text: 'ID партнёра', value: 'invoice_affiliate_id', align: 'center', width: '80px' },
        { text: 'Партнёр', value: 'invoice_affiliate_name', width: 'max-content' },
        { text: 'ID мастера', value: 'invoice_master_id', align: 'center', width: '80px' },
        { text: 'Мастер', value: 'invoice_master_name', width: 'max-content' },
        { text: 'Менеджер', value: 'invoice_manager_name', width: 'max-content' },
        { text: 'Валюта счёта', value: 'invoice_currency', align: 'right', width: '80px' },
        { text: 'Сумма счёта', value: 'invoice_payout', align: 'right', width: 'max-content' },
        { text: 'Способ выплаты', value: 'method_name_ru', width: 'max-content' },
        { text: 'Досрочка', value: 'is_prepayment', align: 'center', width: 'max-content' },
        { text: 'Сумма платежа', value: 'amount', align: 'right', width: 'max-content' },
        { text: 'Комиссия', value: 'commission', align: 'right', width: 'max-content' },
        { text: 'Статус', value: 'is_paid', width: 'max-content' },
    ];

    const detailHeaders: ITableHeader[] = [
        { text: 'ID', value: 'id', align: 'center', width: '80px' },
        { text: 'Способ выплаты', value: 'method_name_ru', width: 'auto' },
        { text: 'Досрочка', value: 'is_prepayment', align: 'center', width: '80px' },
        { text: 'Валюта', value: 'currency', align: 'right', width: '80px' },
        { text: 'Сумма', value: 'amount', align: 'right' },
        { text: 'Комиссия', value: 'commission', align: 'right' },
        { text: 'Статус', value: 'is_paid' },
        { text: 'Комментарий', value: 'note', width: 'auto' },
    ];

    const getHeaders = computed(() => isDetail ? detailHeaders : headers);

    return { getHeaders };
};

export class BillsFilters extends CreatedFilters {
    constructor() {
        super();

        (async () => await this.setEmployeeMethods())();
    }

    getFilters(): IFilter[] {
        return [
            new filterClass.ComboboxArray({
                id: 'ids',
                label: 'ID платежа',
                select: [],
                checkMultiPaste: true,
                multiPaste: true,
            }),
            new filterClass.Multiselect({
                id: 'currencies',
                label: 'Валюта платежа',
                noDataText: 'Нет выбранной валюты',
                items: [],
                itemValue: 'code',
                itemText: 'code',
                type: 'string',
                isCurrencyCatalogue: true,
            }),
            new filterClass.ComboboxArray({
                id: 'invoice_ids',
                label: 'ID счёта',
                select: [],
                checkMultiPaste: true,
                multiPaste: true,
            }),
            new filterClass.Multiselect({
                id: 'invoice_currencies',
                label: 'Валюта счёта',
                noDataText: 'Нет выбранной валюты',
                items: [],
                itemValue: 'code',
                itemText: 'code',
                type: 'string',
                isCurrencyCatalogue: true,
            }),
            new filterClass.Datepicker({
                id: 'period',
                label: 'Период оплаты',
                pickerType: 'date',
                select: [],
                range: true,
                clearable: true,
                allowedDates: () => ({ start: null, end: new Date(), days: [1, 15] }),
            }),
            new filterClass.Autocomplete({
                id: 'master_ids',
                apiMethod: getAffiliateSearch,
                label: 'Мастер-аккаунт',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [],
                multiple: true,
                multiPaste: true,
                alsoSearchBy: 'email',
                getValueForSubmit(filter: IFilter) {
                    return {
                        master_account_id: filter.select.map((item) => item.id),
                    };
                },
            }),
            new filterClass.Autocomplete({
                id: 'affiliate_ids',
                apiMethod: getAffiliateSearch,
                label: 'Партнёр',
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
                select: [],
                items: [],
                multiple: true,
                multiPaste: true,
                getValueForSubmit(filter: IFilter) {
                    return {
                        [filter.id]: filter.select.map((item) => item.id),
                    };
                },
            }),
            new filterClass.Autocomplete({
                id: 'manager_ids',
                apiMethod: getUserSearch,
                label: 'Менеджер',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [],
                remove: true,
                multiple: true,
                pinCurrentUser: true,
                getValueForSubmit(filter: IFilter) {
                    return {
                        [filter.id]: filter.select.map((item) => item.id),
                    };
                },
            }),
            new filterClass.Multiselect({
                id: 'methods',
                type: 'string',
                label: 'Способ выплаты',
                noDataText: 'Нет выбранного способа выплаты',
                select: [],
                items: [],
                itemValue: 'id',
                itemText: (i) => i.name.ru,
                template: (i) => i.name.ru,
            }),
            new filterClass.Select({
                id: 'is_prepayment',
                label: 'Досрочка',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
            }),
            new filterClass.Select({
                id: 'is_paid',
                label: 'Статус',
                items: [
                    { text: 'Оплачен', value: true },
                    { text: 'Не оплачен', value: false },
                ],
                clearable: true,
                type: 'boolean',
            }),
            new filterClass.Datepicker({
                id: 'created',
                label: 'Создан',
                pickerType: 'date',
                select: [],
                range: true,
                clearable: true,
            }),
            new filterClass.Datepicker({
                id: 'updated',
                label: 'Обновлён',
                pickerType: 'date',
                select: [],
                range: true,
                clearable: true,
            }),
        ];
    }

    async setEmployeeMethods(): Promise<void> {
        try {
            const filter = this.filters.find(f => f.id === 'methods')!;
            if (filter?.items?.length!) return;
            const { data } = await getEmployeeMethods();
            filter.items = data;
        } catch (err) {
            showServerError(err, 'Список способов выплат не загружен');
        }
    }
}

type BillItem = IListItem<Bill>
type BillCardName = 'info';
export type BillCards = {
    [key in BillCardName]: BillItem[]
}

export const useBillSingle = (data: Bill): { getCards: () => InfoCards<Bill> } => {
    const info: BillItem[] = [
        {
            itemType: 'route',
            label: 'ID счёта',
            routeName: 'affiliate-invoice',
            routeText: 'invoice_id',
            valueKey: 'invoice_id',
            value: '',
        },
        {
            itemType: 'date',
            label: 'Период оплаты',
            valueKey: 'invoice_pay_period',
            value: '',
        },
        {
            itemType: 'route',
            label: 'Партнёр',
            routeName: 'affiliate',
            routeText: 'invoice_affiliate_id',
            valueKey: 'invoice_affiliate_id',
            additionalText: 'invoice_affiliate_name',
            value: '',
        },
        {
            itemType: 'route',
            label: 'Мастер',
            routeName: 'affiliate',
            routeText: 'invoice_master_id',
            valueKey: 'invoice_master_id',
            additionalText: 'invoice_master_name',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Менеджер',
            valueKey: 'invoice_manager_name',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Валюта счёта',
            valueKey: 'invoice_currency',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Сумма счёта',
            valueKey: 'invoice_payout',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Способ выплаты',
            valueKey: 'method_name_ru',
            value: '',
        },
        {
            itemType: 'boolean',
            label: 'Досрочка',
            valueKey: 'is_prepayment',
            trueText: 'Да',
            falseText: 'Нет',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Сумма платежа',
            valueKey: 'amount',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Комиссия',
            valueKey: 'commission',
            value: '',
        },
        {
            itemType: 'text',
            label: 'Комментарий',
            valueKey: 'note',
            isClamp: false,
            value: '',
        },
    ];

    const cards: BillCards = {
        info,
    };

    const getCards = () => {
        return usePrepareInfoCards<Bill>(cards, data);
    };

    return { getCards };
};
